import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Product } from '../types';
import { productService } from '../services/api';
import { getImageUrl } from '../utils/imageUtils';
import './Product.css';



const ProductPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'image' | '3d'>('image');

  useEffect(() => {
    if (id) {
      fetchProduct(parseInt(id));
    }
  }, [id]);

  const fetchProduct = async (productId: number) => {
    try {
      setLoading(true);
      const products = await productService.getAllProducts();
      const foundProduct = products.find(p => p.id === productId);
      
      if (foundProduct) {
        setProduct(foundProduct);
        setError(null);
      } else {
        setError('Product not found');
      }
    } catch (err) {
      setError('Failed to load product');
      console.error('Error fetching product:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading product...</p>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="error-container">
        <p className="error-message">{error || 'Product not found'}</p>
        <button onClick={() => navigate('/')} className="back-button">
          Back to Home
        </button>
      </div>
    );
  }

  return (
    <div className="product-page">
      <div className="breadcrumb">
        <a href="https://ge-room.com" target="_blank" rel="noopener noreferrer" className="breadcrumb-link">Home</a>
        <span className="breadcrumb-separator">/</span>
        <a href="/" className="breadcrumb-link">Gallery</a>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">{product?.name}</span>
      </div>
      <div className="product-container">
        <div className="product-media">
          <div className="media-container">
            {viewMode === 'image' ? (
              <img 
                src={getImageUrl(product.img_path)} 
                alt={product.name}
                className="product-image"
                onLoad={() => {
                  console.log(`Product image loaded successfully: ${product.name} - ${product.img_path}`);
                }}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  const imageUrl = getImageUrl(product.img_path);
                  console.error(`Failed to load product image:`, {
                    productId: product.id,
                    imgPath: product.img_path,
                    fullUrl: imageUrl,
                    error: e
                  });
                  target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGRkIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPk5vIEltYWdlPC90ZXh0Pjwvc3ZnPg==';
                }}
              />
            ) : (
              React.createElement('model-viewer', {
                src: getImageUrl(product.glb_path),
                'ios-src': getImageUrl(product.usd_path),
                ar: true,
                'ar-placement': 'floor',
                'ar-scale': 'fixed',
                'camera-controls': true,
                'touch-action': 'pan-y',
                alt: `A 3D model of ${product.name}`,
                className: 'model-viewer',
                style: { width: '100%', height: '100%' }
              }, 
                React.createElement('button', {
                  slot: 'ar-button',
                  className: 'ar-button'
                }, 'View in AR')
              )
            )}
          </div>
          
          <div className="view-mode-selector">
            <button 
              className={`mode-button ${viewMode === 'image' ? 'active' : ''}`}
              onClick={() => setViewMode('image')}
            >
              <img 
                src={getImageUrl(product.img_path)} 
                alt="Image view"
                className="mode-preview"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGRkIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPk5vIEltYWdlPC90ZXh0Pjwvc3ZnPg==';
                }}
              />
            </button>
            <button 
              className={`mode-button ${viewMode === '3d' ? 'active' : ''}`}
              onClick={() => setViewMode('3d')}
            >
              <span className="mode-icon">📦</span>
            </button>
          </div>
        </div>

        <div className="product-details">
          <h1 className="product-title">
            <span className="product-name-text">{product.name}</span>
            <span className="product-price-text">€{product.price.toFixed(2)}</span>
          </h1>
          
          <div className="product-specs">
            <div className="spec-group">
              <h3>Dimensions</h3>
              <div className="specs-grid">
                <div className="spec-item">
                  <span className="spec-label">Length:</span>
                  <span className="spec-value">{product.length} cm</span>
                </div>
                <div className="spec-item">
                  <span className="spec-label">Height:</span>
                  <span className="spec-value">{product.height} cm</span>
                </div>
                <div className="spec-item">
                  <span className="spec-label">Depth:</span>
                  <span className="spec-value">{product.depth} cm</span>
                </div>
              </div>
            </div>

            <div className="spec-group">
              <h3>Details</h3>
              <div className="specs-grid">
                <div className="spec-item">
                  <span className="spec-label">Category:</span>
                  <span className="spec-value">{product.category}</span>
                </div>
                <div className="spec-item">
                  <span className="spec-label">Material:</span>
                  <span className="spec-value">{product.material}</span>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default ProductPage;
